import React, { useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { Filter, RotateCcw, Printer, FileDown, FileSpreadsheet } from "lucide-react";
import { API, money, fmtDate } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { StatusBadge } from "../../components/StatusBadge";
import { Button } from "../../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";

const TABS = ["active", "payments", "overdue", "auctions", "inventory", "financial"];
const TAB_ACTIVE = {
  active: "bg-[#1E3A5F] text-white", payments: "bg-emerald-600 text-white",
  overdue: "bg-red-500 text-white", auctions: "bg-amber-500 text-slate-900",
  inventory: "bg-violet-600 text-white", financial: "bg-teal-600 text-white",
};
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const COLUMNS = {
  active: [
    { key: "contract_no", label: "rpt.contract_no", mono: true },
    { key: "item_type", label: "rpt.item_type" },
    { key: "regime", label: "rpt.regime" }, { key: "borrower_kind", label: "rpt.borrower" },
    { key: "item", label: "rpt.item" },
    { key: "loan_amount", label: "rpt.loan_amount", money: true },
    { key: "rate", label: "rpt.rate" },
    { key: "interest_amount", label: "rpt.interest_amount", money: true },
    { key: "contract_date", label: "rpt.contract_date" },
    { key: "due_date", label: "rpt.due_date" },
    { key: "status", label: "common.status", badge: true },
  ],
  payments: [
    { key: "date", label: "common.date" },
    { key: "loan_number", label: "loans.number", mono: true },
    { key: "customer_name", label: "loans.customer" },
    { key: "payment_type", label: "rcp.payment_type", ptype: true },
    { key: "amount", label: "common.amount", money: true, right: true },
  ],
  overdue: [
    { key: "contract_no", label: "rpt.contract_no", mono: true },
    { key: "customer_name", label: "loans.customer" },
    { key: "item", label: "rpt.item" },
    { key: "total_due", label: "loans.total_due", money: true },
    { key: "penalty", label: "rpt.penalties", money: true },
    { key: "days_overdue", label: "alerts.days_overdue" },
    { key: "due_date", label: "rpt.due_date" },
  ],
  auctions: [
    { key: "name", label: "rpt.item" },
    { key: "category", label: "rpt.category" },
    { key: "status", label: "common.status", badge: true },
    { key: "estimated_value", label: "items.est_value", money: true },
    { key: "sale_price", label: "auctions.sale_price", money: true },
    { key: "sold_at", label: "common.date", date: true },
  ],
  inventory: [
    { key: "name", label: "rpt.item" },
    { key: "category", label: "rpt.category" },
    { key: "status", label: "common.status", badge: true },
    { key: "warehouse_status", label: "nav.warehouse" },
    { key: "storage_location", label: "items.location" },
    { key: "estimated_value", label: "items.est_value", money: true },
  ],
  financial: [
    { key: "month", label: "rpt.month", mono: true },
    { key: "interest", label: "repay.interest_part", money: true },
    { key: "principal", label: "repay.principal_part", money: true },
    { key: "total", label: "reports.total_collected", money: true, right: true },
  ],
};

const CARD_DEFS = {
  active: [["total_contracts", "rpt.total_contracts"], ["total_loan", "rpt.total_loan", true], ["interest_acc", "rpt.interest_acc", true], ["almost_expired", "rpt.almost_expired"]],
  payments: [["total_payments", "rpt.total_payments"], ["total_collected", "reports.total_collected", true], ["interest_collected", "rpt.interest_collected", true], ["principal_collected", "rpt.principal_collected", true]],
  overdue: [["overdue_count", "rpt.overdue_count"], ["total_due", "loans.total_due", true], ["penalties", "rpt.penalties", true], ["over60", "rpt.over60"]],
  auctions: [["in_auction", "auctions.in_auction"], ["sold", "auctions.sold"], ["sold_total", "reports.auction_sales", true], ["profit", "reports.auction_profit", true], ["loss", "fin.auction_loss", true]],
  inventory: [["total_items", "rpt.total_items"], ["awaiting", "wh.awaiting"], ["in_warehouse", "wh.in_warehouse"], ["released", "wh.released"]],
  financial: [["interest_earned", "reports.interest_earned", true], ["total_collected", "reports.total_collected", true], ["principal_outstanding", "reports.principal_out", true], ["penalty_collected", "fin.penalty_collected", true], ["auction_sales", "reports.auction_sales", true]],
};

function downloadCsv(filename, columns, rows, t) {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [columns.map((c) => esc(t(c.label))).join(",")];
  rows.forEach((r) => lines.push(columns.map((c) => esc(r[c.key] ?? "")).join(",")));
  const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

export default function Reports() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("active");
  const [month, setMonth] = useState("all");
  const [year, setYear] = useState("all");
  const [category, setCategory] = useState("all");
  const [regime, setRegime] = useState("all");
  const [borrower, setBorrower] = useState("all");
  const [applied, setApplied] = useState({ month: "all", year: "all", category: "all", regime: "all", borrower: "all" });

  const load = (f) => {
    const params = {};
    if (f.month !== "all") params.month = Number(f.month);
    if (f.year !== "all") params.year = Number(f.year);
    if (f.category !== "all") params.category = f.category;
    if (f.regime !== "all") params.item_use = f.regime;
    if (f.borrower !== "all") params.borrower_kind = f.borrower;
    API.get("/shop/reports", { params }).then((r) => setData(r.data)).catch(() => {});
  };
  useEffect(() => { load(applied); /* eslint-disable-next-line */ }, []);

  if (!data) return <p className="text-slate-500">{t("common.loading")}</p>;

  const tabData = data[tab];
  const rows = tab === "financial" ? data.financial.revenue_by_month : tabData.rows;
  const cards = CARD_DEFS[tab].map(([key, label, isMoney]) => ({
    label: t(label),
    value: isMoney ? money(tabData.cards[key]) : tabData.cards[key],
  }));
  const columns = COLUMNS[tab];

  const cellValue = (col, row) => {
    const v = row[col.key];
    if (col.money) return money(v);
    if (col.date) return fmtDate(v);
    if (col.ptype) return v ? t(`pay.type_${v}`) : "—";
    return v ?? "—";
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-semibold text-slate-900">{t("reports.title")}</h1>

      <div className="flex flex-wrap gap-2" data-testid="report-tabs">
        {TABS.map((k) => (
          <button key={k} data-testid={`report-tab-${k}`} onClick={() => setTab(k)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors border ${
              tab === k ? TAB_ACTIVE[k] : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
            }`}>
            {t(`rpt.${k}`)} <span className="ml-1 text-xs opacity-80">{data.counts[k]}</span>
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("rpt.month")}</label>
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="filter-month" className="w-36 h-9 bg-white mt-1"><SelectValue placeholder={t("rpt.month")} /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">—</SelectItem>
              {MONTHS.map((m, i) => <SelectItem key={i + 1} value={String(i + 1)} data-testid={`filter-month-${i + 1}`}>{m}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("rpt.year")}</label>
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="filter-year" className="w-28 h-9 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">—</SelectItem>
              {data.years.map((y) => <SelectItem key={y} value={String(y)} data-testid={`filter-year-${y}`}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("rpt.category")}</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="filter-category" className="w-40 h-9 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              {data.categories.map((c) => <SelectItem key={c} value={c} data-testid={`filter-cat-${c}`}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("flt.regime")}</label>
          <Select value={regime} onValueChange={setRegime}>
            <SelectTrigger data-testid="filter-regime" className="w-44 h-9 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">{t("flt.all")}</SelectItem>
              <SelectItem value="personal" data-testid="filter-regime-personal">{t("ct.use_personal")}</SelectItem>
              <SelectItem value="professional" data-testid="filter-regime-professional">{t("ct.use_professional")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] font-mono uppercase tracking-wider text-slate-500">{t("flt.borrower")}</label>
          <Select value={borrower} onValueChange={setBorrower}>
            <SelectTrigger data-testid="filter-borrower" className="w-36 h-9 bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">{t("flt.all")}</SelectItem>
              <SelectItem value="individual" data-testid="filter-borrower-individual">{t("ct.individual")}</SelectItem>
              <SelectItem value="company" data-testid="filter-borrower-company">{t("ct.company")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button data-testid="filter-apply" onClick={() => { const f = { month, year, category, regime, borrower }; setApplied(f); load(f); }}
          className="bg-[#1E3A5F] hover:bg-[#16294d] text-white h-9">
          <Filter className="h-4 w-4 mr-1.5" />{t("rpt.filter")}
        </Button>
        <Button variant="outline" data-testid="filter-reset" className="h-9"
          onClick={() => { setMonth("all"); setYear("all"); setCategory("all"); setRegime("all"); setBorrower("all"); const f = { month: "all", year: "all", category: "all", regime: "all", borrower: "all" }; setApplied(f); load(f); }}>
          <RotateCcw className="h-4 w-4 mr-1.5" />{t("rpt.reset")}
        </Button>
        <div className="flex-1" />
        <Button variant="outline" data-testid="report-print" className="h-9" onClick={() => window.print()}>
          <Printer className="h-4 w-4 mr-1.5" />{t("repay.print")}
        </Button>
        <Button variant="outline" data-testid="report-pdf" className="h-9 text-red-600 border-red-200 hover:bg-red-50" onClick={() => window.print()}>
          <FileDown className="h-4 w-4 mr-1.5" />{t("rpt.pdf")}
        </Button>
        <Button variant="outline" data-testid="report-excel" className="h-9 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
          onClick={() => downloadCsv(`${tab}-report.csv`, columns, rows, t)}>
          <FileSpreadsheet className="h-4 w-4 mr-1.5" />{t("rpt.excel")}
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c, i) => (
          <div key={i} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`report-card-${i}`}>
            <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{c.label}</div>
            <div className="mt-2 font-heading text-2xl font-bold text-slate-900">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="print-area bg-white rounded-xl border border-slate-200 p-6" data-testid="report-detail">
        <h3 className="font-heading text-lg font-medium text-slate-900 mb-4">
          {t(`rpt.${tab}`)} · {rows.length} {t("rpt.rows")}
        </h3>
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((c) => (
                <TableHead key={c.key} className={c.right ? "text-right" : ""}>{t(c.label)}</TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={columns.length} className="text-center text-slate-500 py-10">{t("common.none")}</TableCell></TableRow>
            )}
            {rows.map((row, i) => (
              <TableRow key={row.id || row.contract_no || i} data-testid={`report-row-${i}`}>
                {columns.map((c) => (
                  <TableCell key={c.key} className={`${c.mono ? "font-mono text-sm" : ""} ${c.right ? "text-right" : ""}`}>
                    {c.badge ? <StatusBadge status={row[c.key]} /> : cellValue(c, row)}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {tab === "financial" && rows.length > 0 && (
          <div className="mt-6">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={rows}>
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
                <Bar dataKey="interest" name={t("repay.interest_part")} fill="#D97706" radius={[4, 4, 0, 0]} />
                <Bar dataKey="principal" name={t("repay.principal_part")} fill="#059669" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {tab === "auctions" && tabData.monthly?.length > 0 && (
          <div className="mt-6" data-testid="auction-monthly-section">
            <h4 className="font-heading text-sm font-semibold text-slate-700 mb-3">{t("rpt.auction_monthly")}</h4>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={tabData.monthly}>
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip formatter={(v) => money(v)} />
                <Legend />
                <Bar dataKey="sales" name={t("reports.auction_sales")} fill="#D97706" radius={[4, 4, 0, 0]} />
                <Bar dataKey="profit" name={t("reports.auction_profit")} fill="#059669" radius={[4, 4, 0, 0]} />
                <Bar dataKey="loss" name={t("fin.auction_loss")} fill="#DC2626" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
            <div className="mt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("rpt.month")}</TableHead>
                    <TableHead>{t("rpt.items_sold")}</TableHead>
                    <TableHead className="text-right">{t("reports.auction_sales")}</TableHead>
                    <TableHead className="text-right">{t("reports.auction_profit")}</TableHead>
                    <TableHead className="text-right">{t("fin.auction_loss")}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {tabData.monthly.map((m) => (
                    <TableRow key={m.month} data-testid={`auction-month-${m.month}`}>
                      <TableCell className="font-mono text-sm">{m.month}</TableCell>
                      <TableCell>{m.items_sold}</TableCell>
                      <TableCell className="text-right font-mono">{money(m.sales)}</TableCell>
                      <TableCell className="text-right font-mono text-emerald-700">{money(m.profit)}</TableCell>
                      <TableCell className="text-right font-mono text-red-600">{m.loss ? money(m.loss) : "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
